import * as React from "react"
import type { Room } from "../../../recoil/atoms/room/types"
import { Flex, Box } from "@chakra-ui/react"
import { useRecoilValue, useRecoilState } from "recoil"
import styled from "@emotion/styled"
import { brandingSelector } from "../../../recoil/atoms/room"
import VideoBadge from "./video-badge"

import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import useWindowSize from "../../../hooks/useWindowSize"
import {
  shoppableVideosListSelector,
  shoppableVideosFilterState
} from "../../../recoil/atoms/shoppableVideos"
import SearchBar from "../badges/components/SearchBar"
import { useTranslation } from "react-i18next"
import { FadeVideos } from "./_fadeVideos"
import { someHaveOnDeviceSelector } from "../../../recoil/selectors/some-have-on-selectors"
import layoutStates from "../../../recoil/atoms/layout"
import { positionFsVideosAtom } from "../../../recoil/atoms/layout/position-fs-control"
import { handleDisableControls } from "../../../util/"
import { waitForElm } from "../../../util/waitForElm"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import useConfig from "src/hooks/useConfig"
import { shoppableVideoConfigToPanelDescriptionText } from "../util"

interface VideosProps {
  isMobile?: boolean
  isTransparent?: boolean
  height?: number
}

type PostionScrollType = "top" | "bottom" | "middle" | "noHasScrollbar"

interface StyledProps {
  room?: Room
  positionscroll?: PostionScrollType
}

const Container = styled(Flex)<StyledProps>`
  -webkit-align-content: flex-start;
  align-content: flex-start;
  --scrollbar-width-product-description: 1px;
  --mask-height-product-description: 30px;
  overflow-y: auto;
  --mask-size-content-product-description: calc(
      100% - var(--scrollbar-width-product-description)
    )
    100%;
  --mask-image-scrollbar-product-description: linear-gradient(black, black);
  --mask-size-scrollbar-product-description: var(
      --scrollbar-width-product-description
    )
    100%;

  mask-image: ${({ positionscroll }) =>
    positionscroll === "noHasScrollbar"
      ? ""
      : `linear-gradient(
          to ${positionscroll === "top" ? "top" : "bottom"},
          transparent,
          black var(--mask-height-product-description),
          black calc(100% - ${
            positionscroll === "middle"
              ? "var(--mask-height-product-description)"
              : "0px"
          }),
          transparent
        ),
        var(--mask-image-scrollbar-product-description)`};

  mask-size: var(--mask-size-content-product-description),
    var(--mask-size-scrollbar-product-description);
  mask-position: 0 0, 100% 0;
  mask-repeat: no-repeat, no-repeat;

  &::-webkit-scrollbar-track {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorder || "#cbd5e0"};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorderOffset || "#fff"};
    border-radius: 10px;
  }
`

export const Videos: React.FC<VideosProps> = React.memo(
  ({ isMobile, isTransparent, height }) => {
    const { t } = useTranslation()
    const { isAudience, isBroadcaster } = useLocalUserType()
    const videosContainerRef = React.useRef<HTMLDivElement>(null)
    const roomStyles = useRecoilValue(brandingSelector)

    const { isLandscape } = useRecoilValue(viewDependingLayoutAtom)
    const { screenSize } = useWindowSize()
    const shoppableVideosList = useRecoilValue(shoppableVideosListSelector)
    const isSomeHaveOnDevice = useRecoilValue(someHaveOnDeviceSelector)
    const [value, setValue] = useRecoilState(shoppableVideosFilterState)
    const isSidePanelWithSingleTab = useRecoilValue(
      layoutStates.isSidePanelWithSingleTab
    )

    const config = useConfig()

    const options = [{ value: "alphabetically", label: t("Alphabetically") }]

    const [positionScroll, setPositionScroll] =
      React.useState<PostionScrollType>("noHasScrollbar")

    const [targetTabIndex] = useRecoilState<number>(
      layoutStates.historyTabEnabledMobile
    )
    const [, setPositionVideosScroll] = useRecoilState(positionFsVideosAtom)

    React.useEffect(() => {
      if (isMobile) {
        const videosContainer = videosContainerRef.current
        const observer = new ResizeObserver(entries => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars

          // eslint-disable-next-line @typescript-eslint/no-unused-vars

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          for (let entry of entries) {
            if (videosContainer.scrollHeight <= videosContainer.clientHeight) {
              setPositionScroll("noHasScrollbar")
              setPositionVideosScroll("middle")
            } else {
              if (videosContainer.scrollTop < 10) {
                setPositionScroll("top")
                setPositionVideosScroll("top")
              } else if (
                videosContainer?.scrollTop >=
                videosContainer?.scrollHeight -
                  videosContainer?.clientHeight -
                  10
              ) {
                setPositionScroll("bottom")
                setPositionVideosScroll("bottom")
              } else {
                setPositionScroll("middle")
                setPositionVideosScroll("middle")
              }
            }
          }
        })

        if (videosContainer) {
          observer.observe(videosContainer)
        }

        return () => {
          if (videosContainer) {
            observer.unobserve(videosContainer)
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videosContainerRef, isTransparent, targetTabIndex])

    React.useEffect(() => {
      if (isMobile) {
        const videosContainer = videosContainerRef.current

        if (!videosContainer) return

        const handleScroll = () => {
          if (videosContainer.scrollHeight > videosContainer.clientHeight) {
          }
          if (
            videosContainer.scrollTop < 10 &&
            videosContainer.scrollTop !== 0
          ) {
            setPositionScroll("top")
            setPositionVideosScroll("top")
          } else if (
            videosContainer?.scrollTop >=
              videosContainer?.scrollHeight -
                videosContainer?.clientHeight -
                10 &&
            videosContainer.scrollTop !== 0
          ) {
            setPositionScroll("bottom")
            setPositionVideosScroll("bottom")
          } else {
            if (videosContainer.scrollTop !== 0) {
              setPositionScroll("middle")
              setPositionVideosScroll("middle")
            }
          }

          handleDisableControls("videos-container")
        }

        videosContainer.addEventListener("scroll", handleScroll, false)
        return () =>
          videosContainer.removeEventListener("scroll", handleScroll, false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTransparent, targetTabIndex])

    const [firstOpen, setFirstOpen] = React.useState(true)

    const showInBottom = async () => {
      setFirstOpen(true)
      const container = (await waitForElm("videos-container")) as HTMLDivElement

      container.scrollTop = container.scrollHeight
      setFirstOpen(false)
    }

    React.useEffect(() => {
      if (isLandscape) {
        let timerForScroll = setTimeout(() => {
          showInBottom()
        }, 300)
        return () => clearTimeout(timerForScroll)
      }
    }, [isLandscape])

    React.useEffect(() => {
      if (isMobile) {
        if (targetTabIndex === 0) {
          setFirstOpen(true)
        }

        if (targetTabIndex === 1) {
          videosContainerRef.current.scrollTop =
            videosContainerRef.current.scrollHeight

          let timer = setTimeout(() => {
            setFirstOpen(false)
          }, 1000)
          return () => clearTimeout(timer)
        }
      }
    }, [targetTabIndex, isTransparent, isLandscape, isMobile])

    return (
      <>
        {!isMobile && (
          <SearchBar
            value={value}
            setValue={setValue}
            customOptions={options}
          />
        )}
        <Box
          p={isMobile ? "0rem" : "1rem 0.5rem 1rem 0"}
          height="100%"
          overflowX="hidden"
          overflowY="hidden"
          paddingBottom={isSidePanelWithSingleTab ? "70px" : undefined} // 70px to make same as chat list
        >
          {/* Replace this container with ScrollableBlurList */}
          <Container
            display={isMobile ? "flow-root" : "block"}
            flexDir="column"
            alignItems="center"
            overflow="auto"
            id="videos-container"
            positionscroll={positionScroll}
            ref={videosContainerRef}
            zIndex={isMobile ? "10000" : "unset"}
            width={
              isLandscape ? 400 : isTransparent ? screenSize.width - 60 : "100%"
            }
            padding={
              isTransparent ? "0px" : isMobile ? "10px" : "0 0.5rem 0 1rem"
            }
            roomStyles={roomStyles}
            height="100%"
            style={firstOpen ? {} : { scrollBehavior: "smooth" }}
          >
            {shoppableVideosList.length === 0 && !isTransparent && (
              <FadeVideos
                isSmallView={isMobile}
                infoText={
                  `This is where ${shoppableVideoConfigToPanelDescriptionText(config.shoppableVideos)} added via the Dashboard will appear`
                }
                customStyles={!isMobile && { marginBottom: "10px" }}
              />
            )}
            {isSomeHaveOnDevice && shoppableVideosList.length !== 0 && (
              <Flex
                padding="0.5rem"
                border="2px solid"
                borderRadius="0.5rem"
                width={isMobile && "100%"}
                lineHeight={1.2}
                fontSize={"0.875rem"}
                color={roomStyles.servicePanelElementPrimaryText ?? "#495057"}
                borderColor={roomStyles?.servicePanelElementBorder ?? "#E2E8F0"}
                background={
                  roomStyles?.servicePanelElementBackground ?? "white"
                }
                marginBottom={"10px"}
              >
                {
                  "You'll be able to watch these videos when there are no presenters streaming audio or video."
                }
              </Flex>
            )}

            {shoppableVideosList.map((item, index) => {
              
              // Hide the video badge from the audience if 'SV' is not set in the dashboard.
              if ((isAudience || isBroadcaster) && !item.shoppableEnabled) {
                return null
              }

              return (
                <React.Fragment key={`${index}-${item?.id}`}>
                  <VideoBadge
                    {...item}
                    isMobile={isMobile}
                    isTransparent={isTransparent}
                    isSomeHaveOnDevice={isSomeHaveOnDevice}
                    isLast={index === shoppableVideosList.length - 1}
                  />
                </React.Fragment>
              )
            })}
          </Container>
        </Box>
      </>
    )
  }
)

export default Videos
